import {UTCDate} from '@date-fns/utc';
import {z} from 'zod';

import {Units} from '../../types/enums';

import {slimUserSchema} from './slimUserSchema';

export enum ReportStatus {
  Draft,
  Queued,
  ImageProcessing,
  PrintProcessing,
  Failed,
  Completed,
}

export enum ReportAnnotationsColumns {
  Slug = 1,
  Severity = 2,
  Category = 4,
  Tags = 8,
  Measurements = 16,
  Findings = 32,
  Locations = 64,
}

export enum AnnotationColourSchema {
  Category,
  Severity,
}

export enum ReportAnnotationsFormat {
  Full,
  Lite,
  Merged,
}

export const reportSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  modified: z.coerce.date(),
  status: z.enum(ReportStatus),

  coverPage: z.object({
    isEnabled: z.boolean(),
    title: z.string(),
    date: z.string().transform((str) => new UTCDate(str)),
    companyName: z.string(),
    companyAddress: z.string(),
    reviewedBy: z.string(),
    inspectedBy: z.string(),
    companyLogo: z.string().nullable(),
    coverImage: z.string().nullable(),
  }),

  overview: z.object({
    isEnabled: z.boolean(),
    summary: z.string(),
    enableCaptureData: z.boolean(),
  }),

  tableOfContents: z.object({
    isEnabled: z.boolean(),
  }),

  annotationDetails: z.object({
    format: z.enum(ReportAnnotationsFormat),
    shouldForceFlagged: z.boolean(),
    columns: z.number().int(), // bit flag of ReportAnnotationsColumns
    units: z.enum(Units),
    colourSchema: z.enum(AnnotationColourSchema),
    handheld: z
      .object({
        count: z.enum(['none', 'single', 'multiple']),
        startDate: z.string().nullable(),
        endDate: z.string().nullable(),
      })
      .transform((handheld) => ({
        ...handheld,
        shouldUseRecent: !handheld.startDate && !handheld.endDate,
      })),
    annoIds: z.string().array(),
  }),

  screenshots: z.object({
    isEnabled: z.boolean(),
    values: z.object({id: z.string(), path: z.string()}).array(),
  }),

  documents: z.object({
    isEnabled: z.boolean(),
    documentIds: z.string().array(),
  }),
});

export const reportListSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    status: z.enum(ReportStatus),
    modified: z.coerce.date(),
    path: z.string().nullable(),
    createdBy: slimUserSchema,
  })
  .array();
